<template>
  <section id="hero" class="home-hero">
    <div class="home-hero__soft-orb home-hero__soft-orb--one" />
    <div class="home-hero__soft-orb home-hero__soft-orb--two" />

    <v-container class="home-hero__container">
      <v-row align="center" class="home-hero__row">
        <v-col cols="12" md="6">
          <div class="home-hero__content" data-aos="fade-up">

            <h1>Accurate diagnostic care, made accessible for everyday patients.</h1>
            <p>
              Mayon Clinical Laboratory & Medical Services helps patients, families, employees, companies, and partner
              clinics plan laboratory tests, medical packages, drug testing, imaging inquiries, vaccines, and consultations
              with confidence.
            </p>

            <div class="home-hero__actions">
              <v-btn class="mayon-btn mayon-btn--primary" large :to="{ name: 'home', hash: '#packages' }">
                View packages →
              </v-btn>
              <v-btn class="mayon-btn mayon-btn--secondary" large :to="{ name: 'home', hash: '#services' }">
                Check price guide
              </v-btn>
              <v-btn class="home-hero__link mayon-btn mayon-btn--ghost" text large to="/branches">
                Get directions →
              </v-btn>
            </div>
          </div>
        </v-col>

        <v-col cols="12" md="6">
          <div class="home-hero__visual" data-aos="fade-left">
            <div class="hero-shape hero-shape--teal"></div>
            <div class="hero-shape hero-shape--blue"></div>
            <div class="hero-orbit hero-orbit--one"></div>
            <div class="hero-orbit hero-orbit--two"></div>
            <i class="pi pi-plus hero-medical-mark hero-medical-mark--plus" aria-hidden="true"></i>
            <i class="pi pi-sparkles hero-medical-mark hero-medical-mark--spark" aria-hidden="true"></i>

            <div class="hero-image-carousel" aria-label="Mayon clinic image carousel">
              <div class="hero-image-carousel__peek hero-image-carousel__peek--left" aria-hidden="true">
                <v-img :src="previousHeroImage.src" :alt="previousHeroImage.alt" height="100%" position="center center" />
              </div>

              <v-card class="clinic-visual-card" elevation="0">
                <div class="clinic-visual-card__image">
                  <v-img
                    :key="activeHeroImage.src"
                    :src="activeHeroImage.src"
                    :alt="activeHeroImage.alt"
                    height="100%"
                    position="center center"
                    class="clinic-visual-card__active-image"
                  />
                

                  <div class="clinic-visual-card__logo-pill">
                    <v-img :src="require('@/assets/landingpage/logo2.png')" alt="Mayon logo" contain height="54" width="54" />
                    <span>
                      <strong>Mayon</strong>
                      <small>Clinical Laboratory & Medical Services</small>
                    </span>
                  </div>
                </div>
              </v-card>

              <div class="hero-image-carousel__peek hero-image-carousel__peek--right" aria-hidden="true">
                <v-img :src="nextHeroImage.src" :alt="nextHeroImage.alt" height="100%" position="center center" />
              </div>
            </div>

            <div class="hero-image-carousel__dots" aria-label="Clinic image slide indicators">
              <button
                v-for="(item, index) in heroImages"
                :key="`${item.alt}-dot`"
                type="button"
                :class="{ 'is-active': activeHeroImageIndex === index }"
                :aria-label="`Show clinic image ${index + 1}`"
                @click="setHeroImageSlide(index)"
              />
            </div>

            <div class="clinic-proof-panel">
              <div class="clinic-proof-panel__item" v-for="item in valueProofs" :key="item.label">
                <strong>{{ item.value }}</strong>
                <span>{{ item.label }}</span>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </section>
</template>

<script>
import { valueProofs } from '@/data/mayonSite'

const heroServiceSlides = [
  { label: 'Laboratory tests', icon: 'pi pi-filter' },
  { label: 'Drug testing', icon: 'pi pi-shield' },
  { label: 'Imaging inquiries', icon: 'pi pi-image' },
  { label: 'Vaccines', icon: 'pi pi-send' },
  { label: 'HMO partners', icon: 'pi pi-users' }
]

const heroTrustHighlights = [
  { label: 'DOH-accredited laboratory', icon: 'pi pi-shield' },
  { label: 'ISO 9001:2015 certified', icon: 'pi pi-verified' },
  { label: 'Serving patients since 2004', icon: 'pi pi-calendar' }
]

export default {
  name: 'HeroSection',
  data() {
    return {
      activeHeroImageIndex: 0,
      activeServiceSlide: 0,
      carouselTimer: null,
      serviceTimer: null,
      heroServiceSlides,
      heroTrustHighlights,
      heroImages: [
  {
    src: require('@/assets/hero/1.jpg'),
    alt: 'Entrance of Mayon Clinical Laboratory and Medical Services',
    position: 'center center'
  },
  {
    src: require('@/assets/hero/2.jpg'),
    alt: 'MayonLab reception team assisting patients at the branch',
    position: 'center center'
  },
  {
    src: require('@/assets/hero/3.jpg'),
    alt: 'MayonLab healthcare professional assisting a patient during blood collection',
    position: 'center center'
  },
  {
    src: require('@/assets/hero/4.jpg'),
    alt: 'MayonLab laboratory professional operating diagnostic equipment',
    position: 'center center'
  },
  {
    src: require('@/assets/hero/5.jpg'),
    alt: 'MayonLab medical technologist examining a laboratory sample through a microscope',
    position: 'center center'
  },
  {
    src: require('@/assets/hero/6.jpg'),
    alt: 'MayonLab laboratory team working with automated diagnostic systems',
    position: 'center center'
  }
],
      valueProofs: valueProofs.map((item, index) => ({
        ...item,
        icon: ['pi pi-users', 'pi pi-shield', 'pi pi-verified'][index] || 'pi pi-check-circle'
      }))
    }
  },
  computed: {
    activeHeroImage() {
      return this.heroImages[this.activeHeroImageIndex]
    },
    previousHeroImage() {
      const index = (this.activeHeroImageIndex - 1 + this.heroImages.length) % this.heroImages.length
      return this.heroImages[index]
    },
    nextHeroImage() {
      const index = (this.activeHeroImageIndex + 1) % this.heroImages.length
      return this.heroImages[index]
    }
  },
  mounted() {
    if (this.prefersReducedMotion()) return

    this.carouselTimer = window.setInterval(this.nextHeroImageSlide, 5200)
    this.serviceTimer = window.setInterval(this.nextServiceSlide, 4200)
  },
  beforeDestroy() {
    window.clearInterval(this.carouselTimer)
    window.clearInterval(this.serviceTimer)
  },
  methods: {
    prefersReducedMotion() {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    },
    setHeroImageSlide(index) {
      this.activeHeroImageIndex = index
    },
    nextHeroImageSlide() {
      this.activeHeroImageIndex = (this.activeHeroImageIndex + 1) % this.heroImages.length
    },
    previousHeroImageSlide() {
      this.activeHeroImageIndex = (this.activeHeroImageIndex - 1 + this.heroImages.length) % this.heroImages.length
    },
    setServiceSlide(index) {
      this.activeServiceSlide = index
    },
    nextServiceSlide() {
      this.activeServiceSlide = (this.activeServiceSlide + 1) % this.heroServiceSlides.length
    },
    previousServiceSlide() {
      this.activeServiceSlide = (this.activeServiceSlide - 1 + this.heroServiceSlides.length) % this.heroServiceSlides.length
    }
  }
}
</script>

<style lang="scss" scoped>
.home-hero {
  background:
    radial-gradient(circle at 7% 26%, rgba(88, 184, 184, 0.20), transparent 24rem),
    radial-gradient(circle at 92% 72%, rgba(72, 112, 200, 0.14), transparent 25rem),
    linear-gradient(135deg, #f8fafc 0%, #eef7f8 48%, #ffffff 100%);
  display: flex;
  min-height: calc(100vh - 76px);
  overflow: hidden;
  padding: 2rem 2rem;
  position: relative;

  &::before,
  &::after {
    border: 1px solid rgba(72, 112, 200, 0.10);
    border-radius: 999px;
    content: "";
    pointer-events: none;
    position: absolute;
  }

  &::before {
    height: 44rem;
    left: -17rem;
    top: 4rem;
    width: 44rem;
  }

  &::after {
    bottom: -19rem;
    height: 50rem;
    right: 3rem;
    width: 50rem;
  }

  &__soft-orb {
    border-radius: 999px;
    filter: blur(6px);
    opacity: 0.7;
    position: absolute;

    &--one {
      background: rgba(88, 184, 184, 0.14);
      height: 28rem;
      left: -8rem;
      top: 4rem;
      width: 28rem;
    }

    &--two {
      background: rgba(72, 112, 200, 0.10);
      bottom: 1rem;
      height: 24rem;
      right: -7rem;
      width: 24rem;
    }
  }

  &__container {
    position: relative;
    z-index: 1;
  }

  &__row {
    min-height: 630px;
  }

  &__content {
    max-width: 720px;
    position: relative;
    z-index: 4;

    h1 {
      color: var(--mayon-navy);
      font-size: clamp(2.7rem, 5.75vw, 4.9rem);
      letter-spacing: -0.05em;
      line-height: 1.02;
      margin: 2.25rem 0 1.25rem;
    }

    h1::after {
      color: var(--mayon-teal);
      content: "";
    }

    p {
      color: #475467;
      font-size: clamp(1rem, 1.55vw, 1.14rem);
      line-height: 1.85;
      margin: 0;
      max-width: 650px;
    }
  }

  &__actions {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 0.8rem;
    margin-top: 2rem;
  }

  &__link {
    font-weight: 800;
  }

  &__trust-strip {
    align-items: center;
    background: rgba(255, 255, 255, 0.88);
    border: 1px solid rgba(227, 234, 242, 0.9);
    border-radius: 22px;
    box-shadow: 0 18px 40px rgba(10, 27, 52, 0.06);
    display: inline-flex;
    flex-wrap: wrap;
    gap: 0;
    margin-top: 1.9rem;
    overflow: hidden;
  }

  &__visual {
    isolation: isolate;
    margin-left: auto;
    max-width: 620px;
    min-height: 610px;
    position: relative;
    width: min(100%, 620px);
    z-index: 2;
  }
}

.hero-service-carousel {
  align-items: center;
  background: rgba(255, 255, 255, 0.58);
  border: 1px solid rgba(227, 234, 242, 0.86);
  border-radius: 999px;
  box-shadow: 0 18px 44px rgba(10, 27, 52, 0.06);
  display: inline-flex;
  gap: 0.6rem;
  max-width: 100%;
  padding: 0.55rem;

  &__track {
    display: flex;
    gap: 0.55rem;
    overflow: hidden;
  }

  &__nav {
    align-items: center;
    background: #ffffff;
    border: 1px solid rgba(227, 234, 242, 0.96);
    border-radius: 50%;
    box-shadow: 0 10px 22px rgba(10, 27, 52, 0.08);
    color: var(--mayon-blue);
    cursor: pointer;
    display: inline-flex;
    flex: 0 0 auto;
    font-size: 1.45rem;
    font-weight: 900;
    height: 38px;
    justify-content: center;
    line-height: 1;
    transition: transform 0.2s ease, color 0.2s ease;
    width: 38px;

    &:hover,
    &:focus {
      color: var(--mayon-teal);
      transform: translateY(-1px);
    }
  }

  &__dots {
    display: flex;
    gap: 0.32rem;
    margin: 0.45rem 0 0 6rem;

    span {
      background: rgba(72, 112, 200, 0.14);
      border-radius: 999px;
      height: 5px;
      transition: width 0.22s ease, background 0.22s ease;
      width: 8px;
    }

    span.is-active {
      background: var(--mayon-blue);
      width: 28px;
    }
  }
}

.hero-service-pill {
  align-items: center;
  background: rgba(255, 255, 255, 0.82);
  border: 1px solid rgba(227, 234, 242, 0.95);
  border-radius: 16px;
  color: #344054;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto;
  gap: 0.55rem;
  min-height: 52px;
  padding: 0.55rem 0.82rem;
  text-align: left;
  transition: border-color 0.22s ease, box-shadow 0.22s ease, color 0.22s ease, transform 0.22s ease;

  i {
    color: #667085;
    font-size: 1.1rem;
  }

  span {
    font-size: 0.78rem;
    font-weight: 900;
    line-height: 1.12;
    max-width: 78px;
  }

  &.is-active,
  &:hover,
  &:focus {
    border-color: rgba(72, 112, 200, 0.36);
    box-shadow: 0 12px 28px rgba(72, 112, 200, 0.12);
    color: var(--mayon-blue);
    transform: translateY(-1px);

    i {
      color: var(--mayon-blue);
    }
  }
}

.trust-strip-item {
  align-items: center;
  color: #344054;
  display: flex;
  font-size: 0.88rem;
  font-weight: 800;
  gap: 0.6rem;
  line-height: 1.25;
  min-height: 66px;
  padding: 0.7rem 1rem;
  position: relative;

  &:not(:last-child)::after {
    background: rgba(102, 112, 133, 0.22);
    bottom: 0.9rem;
    content: "";
    position: absolute;
    right: 0;
    top: 0.9rem;
    width: 1px;
  }

  &__icon {
    align-items: center;
    border: 1px solid rgba(88, 184, 184, 0.25);
    border-radius: 14px;
    color: var(--mayon-teal);
    display: inline-flex;
    flex: 0 0 auto;
    font-size: 1.25rem;
    height: 38px;
    justify-content: center;
    width: 38px;
  }
}

.hero-shape,
.hero-orbit,
.hero-medical-mark {
  pointer-events: none;
  position: absolute;
}

.hero-shape {
  border-radius: 42% 58% 44% 56% / 48% 38% 62% 52%;
  filter: saturate(1.04);

  &--teal {
    background: linear-gradient(135deg, rgba(88, 184, 184, 0.34), rgba(255, 255, 255, 0.28));
    height: 430px;
    right: -105px;
    top: -15px;
    transform: rotate(-10deg);
    width: 520px;
  }

  &--blue {
    background: linear-gradient(145deg, rgba(72, 112, 200, 0.84), rgba(88, 184, 184, 0.34));
    height: 430px;
    right: -128px;
    top: 84px;
    transform: rotate(14deg);
    width: 430px;
  }
}

.hero-orbit {
  border: 1px solid rgba(255, 255, 255, 0.58);
  border-radius: 999px;

  &--one {
    height: 490px;
    right: -45px;
    top: 4px;
    transform: rotate(-16deg);
    width: 580px;
  }

  &--two {
    border-color: rgba(72, 112, 200, 0.14);
    height: 420px;
    right: 6px;
    top: 120px;
    transform: rotate(27deg);
    width: 480px;
  }
}

.hero-medical-mark {
  color: rgba(255, 255, 255, 0.78);
  font-size: 1.25rem;
  z-index: 1;

  &--plus {
    color: rgba(88, 184, 184, 0.28);
    font-size: 1.45rem;
    left: -38px;
    top: 310px;
  }

  &--spark {
    right: 68px;
    top: 66px;
  }
}

.hero-image-carousel {
  height: 350px;
  margin-left: auto;
  position: relative;
  top: 86px;
  width: min(100%, 575px);
  z-index: 2;

  &__peek {
    border: 8px solid rgba(255, 255, 255, 0.92);
    border-radius: 26px;
    box-shadow: 0 18px 44px rgba(10, 27, 52, 0.13);
    height: 260px;
    opacity: 0.64;
    overflow: hidden;
    position: absolute;
    top: 45px;
    width: 178px;
    z-index: 1;

    &::after {
      background: rgba(10, 27, 52, 0.08);
      content: "";
      inset: 0;
      position: absolute;
    }

    &--left {
      left: -62px;
    }

    &--right {
      right: -42px;
    }
  }

  &__arrow {
    align-items: center;
    background: rgba(255, 255, 255, 0.96);
    border: 1px solid rgba(227, 234, 242, 0.9);
    border-radius: 50%;
    box-shadow: 0 14px 26px rgba(10, 27, 52, 0.14);
    color: var(--mayon-navy);
    cursor: pointer;
    display: inline-flex;
    font-size: 1.8rem;
    font-weight: 900;
    height: 52px;
    justify-content: center;
    line-height: 1;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    transition: color 0.2s ease, transform 0.2s ease;
    width: 52px;
    z-index: 4;

    &:hover,
    &:focus {
      color: var(--mayon-blue);
      transform: translateY(-50%) scale(1.04);
    }

    &--left {
      left: -27px;
    }

    &--right {
      right: -27px;
    }
  }

  &__dots {
    align-items: center;
    display: flex;
    gap: 0.7rem;
    justify-content: center;
    margin: 4.95rem 0 0;
    position: relative;
    z-index: 3;

    button {
      background: rgba(102, 112, 133, 0.22);
      border: 0;
      border-radius: 999px;
      cursor: pointer;
      height: 8px;
      transition: background 0.2s ease, width 0.2s ease;
      width: 28px;
    }

    button.is-active {
      background: var(--mayon-blue);
      width: 42px;
    }
  }
}

.clinic-visual-card {
  background: #ffffff !important;
  border: 8px solid rgba(255, 255, 255, 0.94) !important;
  border-radius: 34px !important;
  box-shadow: 0 24px 60px rgba(10, 27, 52, 0.16) !important;
  overflow: hidden;
  padding: 0;
  position: relative;
  z-index: 2;

  &__image {
    height: 335px;
    overflow: hidden;
    position: relative;
  }

  &__active-image {
    animation: heroImageReveal 0.55s ease both;
  }

  &__logo-pill {
    align-items: center;
    backdrop-filter: blur(14px);
    background: rgba(255, 255, 255, 0.78);
    border: 1px solid rgba(255, 255, 255, 0.72);
    border-radius: 999px;
    bottom: 1rem;
    box-shadow: 0 12px 24px rgba(10, 27, 52, 0.12);
    display: flex;
    gap: 0.65rem;
    left: 1rem;
    max-width: min(78%, 330px);
    padding: 0.45rem 1.1rem 0.45rem 0.45rem;
    position: absolute;

    span {
      display: flex;
      flex-direction: column;
      line-height: 1.05;
    }

    strong {
      color: var(--mayon-navy);
      font-size: 0.95rem;
    }

    small {
      color: var(--mayon-muted);
      font-size: 0.68rem;
      font-weight: 800;
      letter-spacing: 0.01em;
      text-transform: uppercase;
    }
  }
}

.clinic-proof-panel {
  background: linear-gradient(135deg, var(--mayon-teal) 0%, #3aaec5 48%, var(--mayon-blue) 100%);
  border-radius: 26px;
  box-shadow: 0 22px 48px rgba(72, 112, 200, 0.20);
  color: #ffffff;
  display: grid;
  gap: 0;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin: 8.35rem 0 0 auto;
  max-width: 525px;
  overflow: hidden;
  padding: 1.4rem 1rem;
  position: relative;
  z-index: 2;

  &__item {
    align-items: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 104px;
    padding: 0 0.9rem;
    position: relative;
    text-align: center;

    &:not(:last-child)::after {
      background: rgba(255, 255, 255, 0.34);
      bottom: 0.45rem;
      content: "";
      position: absolute;
      right: 0;
      top: 1.15rem;
      width: 1px;
    }
  }

  &__icon {
    align-items: center;
    background: rgba(255, 255, 255, 0.94);
    border-radius: 50%;
    color: var(--mayon-blue);
    display: inline-flex;
    height: 38px;
    justify-content: center;
    margin-bottom: 0.55rem;
    width: 38px;
  }

  strong,
  span {
    display: block;
  }

  strong {
    font-size: 1.9rem;
    letter-spacing: -0.04em;
    line-height: 1;
  }

  span {
    font-size: 0.75rem;
    font-weight: 900;
    line-height: 1.25;
    margin-top: 0.4rem;
    text-transform: uppercase;
  }
}

@keyframes heroImageReveal {
  from {
    opacity: 0;
    transform: scale(1.025);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .clinic-visual-card__active-image {
    animation: none;
  }

  .hero-service-pill,
  .hero-image-carousel__arrow,
  .hero-service-carousel__nav {
    transition: none;
  }
}

@media (max-width: 1400px) {
  .home-hero {
    padding-left: 1.25rem;
    padding-right: 1.25rem;

    &__content {
      max-width: 620px;
    }

    &__content h1 {
      font-size: clamp(2.8rem, 5.1vw, 4.25rem);
    }

    &__visual {
      max-width: 520px;
      min-height: 585px;
      overflow: visible;
    }
  }

  .hero-service-carousel {
    &__track {
      max-width: 520px;
      overflow-x: auto;
      scrollbar-width: none;

      &::-webkit-scrollbar {
        display: none;
      }
    }
  }

  .hero-shape--teal {
    height: 385px;
    right: -96px;
    top: 10px;
    width: 455px;
  }

  .hero-shape--blue {
    height: 365px;
    right: -112px;
    top: 108px;
    width: 360px;
  }

  .hero-orbit--one {
    height: 430px;
    right: -54px;
    top: 20px;
    width: 500px;
  }

  .hero-orbit--two {
    height: 360px;
    right: -10px;
    top: 145px;
    width: 410px;
  }

  .hero-image-carousel {
    height: 330px;
    top: 94px;
    width: min(100%, 500px);

    &__peek {
      display: none;
    }

    &__arrow {
      height: 46px;
      width: 46px;

      &--left {
        left: 0.75rem;
      }

      &--right {
        right: 0.75rem;
      }
    }

    &__dots {
      margin-top: 4.05rem;
    }
  }

  .clinic-visual-card {
    border-width: 7px !important;

    &__image {
      height: 312px;
    }
  }

  .clinic-proof-panel {
    margin-top: 7.35rem;
    max-width: 485px;
  }
}

@media (max-width: 1080px) and (min-width: 961px) {
  .home-hero {
    &__content {
      max-width: 560px;
    }

    &__content h1 {
      font-size: clamp(2.55rem, 5vw, 3.75rem);
      letter-spacing: -0.055em;
    }

    &__content p {
      font-size: 1rem;
      line-height: 1.75;
    }

    &__visual {
      max-width: 470px;
      min-height: 545px;
    }
  }

  .hero-image-carousel {
    height: 305px;
    top: 92px;
    width: min(100%, 445px);

    &__dots {
      margin-top: 3.6rem;
    }
  }

  .clinic-visual-card {
    border-radius: 28px !important;

    &__image {
      height: 288px;
    }

    &__logo-pill {
      max-width: min(82%, 290px);
    }
  }

  .clinic-proof-panel {
    margin-top: 6.75rem;
    max-width: 435px;
    padding: 1.15rem 0.75rem;

    strong {
      font-size: 1.65rem;
    }

    span {
      font-size: 0.68rem;
    }
  }
}

@media (max-width: 960px) {
  .home-hero {
    min-height: auto;
    padding: 5rem 0 4rem;
    text-align: center;

    &__row {
      min-height: auto;
    }

    &__actions,
    &__trust-strip {
      justify-content: center;
    }

    &__content {
      margin-left: auto;
      margin-right: auto;
    }

    &__content h1 {
      margin-top: 1.7rem;
    }

    &__visual {
      margin: 2.5rem auto 0;
      min-height: auto;
    }
  }

  .hero-service-carousel {
    border-radius: 28px;
    width: 100%;

    &__track {
      flex: 1 1 auto;
    }

    &__dots {
      justify-content: center;
      margin-left: 0;
    }
  }

  .hero-image-carousel {
    top: 0;
  }

  .clinic-proof-panel {
    margin: 5.4rem auto 0;
  }
}

@media (max-width: 600px) {
  .home-hero {
    padding-top: 4.2rem;

    &__actions .v-btn {
      width: 100%;
    }

    &__trust-strip {
      border-radius: 20px;
      display: grid;
      width: 100%;
    }
  }

  .hero-service-carousel {
    gap: 0.4rem;
    padding: 0.45rem;

    &__nav {
      height: 34px;
      width: 34px;
    }
  }

  .hero-service-pill {
    min-height: 48px;
    padding: 0.5rem 0.7rem;
  }

  .trust-strip-item {
    justify-content: flex-start;

    &:not(:last-child)::after {
      bottom: 0;
      height: 1px;
      left: 1rem;
      right: 1rem;
      top: auto;
      width: auto;
    }
  }

  .hero-shape--teal {
    height: 290px;
    right: -120px;
    top: 5px;
    width: 360px;
  }

  .hero-shape--blue {
    height: 280px;
    right: -140px;
    top: 80px;
    width: 300px;
  }

  .hero-image-carousel {
    height: 280px;
    width: 100%;

    &__peek {
      display: none;
    }

    &__arrow {
      height: 44px;
      width: 44px;

      &--left {
        left: 0.55rem;
      }

      &--right {
        right: 0.55rem;
      }
    }

    &__dots {
      margin-top: 1rem;
    }
  }

  .clinic-visual-card {
    border-width: 6px !important;
    border-radius: 26px !important;

    &__image {
      height: 260px;
    }

    &__logo-pill {
      max-width: calc(100% - 2rem);

      small {
        display: none;
      }
    }
  }

  .clinic-proof-panel {
    grid-template-columns: 1fr;
    margin-top: 2.8rem;
    max-width: 100%;
    padding: 0.5rem;

    &__item {
      min-height: auto;
      padding: 1rem;

      &:not(:last-child)::after {
        bottom: 0;
        height: 1px;
        left: 1rem;
        right: 1rem;
        top: auto;
        width: auto;
      }
    }
  }
}
</style>
